import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import pako from "pako";
import { useAuth } from "../firebase/auth.tsx";
import { API_BASE_URL } from "../config.tsx";

import { getUserDataById } from "./helpers/requests.tsx";
import { useTranslation } from "react-i18next";

// Declare FMVP8Player for TypeScript
declare global {
  interface Window {
    FMVP8Player: any;
  }
}

interface StreamProps {
  fullScreenMode: boolean;
  setFullScreenMode: (mode: boolean) => void;
}

const Stream: React.FC<StreamProps> = ({
  fullScreenMode,
  setFullScreenMode,
}) => {
  const { t, i18n } = useTranslation();
  const { userId, token } = useAuth();
  const { streamingUserId } = useParams();

  const [serverConnected, setServerConnected] = useState(false);
  const [isReceivingData, setIsReceivingData] = useState(false);
  const [displayTextKey, setDisplayTextKey] = useState("Stream_is_offline");
  const [displayText, setDisplayText] = useState(t("Stream_is_offline"));
  const [streamingUserData, setStreamingUserData] = useState<any>([]);

  // Refs for streaming state to avoid re-renders and stale closures
  const wsRef = useRef<WebSocket | null>(null);
  const dataID_img = useRef(0);
  const dataLength_img = useRef(0);
  const receivedLength_img = useRef(0);
  const dataByte_img = useRef<Uint8Array>(new Uint8Array(0));
  const ReadyToGetFrame_img = useRef(true);

  // Audio Refs
  const dataID_aud = useRef(0);
  const dataLength_aud = useRef(0);
  const receivedLength_aud = useRef(0);
  const dataByte_aud = useRef<Uint8Array>(new Uint8Array(0));
  const ReadyToGetFrame_aud = useRef(true);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const audioStartTimeRef = useRef(0);

  const label_img = 1001;
  const label_aud = 2001;

  const lastDataTimeRef = useRef(Date.now());
  const metrics = useRef({
    bytesReceived: 0,
    framesRendered: 0,
    framesDropped: 0
  });

  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const vp8CanvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [displayMode, setDisplayMode] = useState<'none' | 'img' | 'vp8'>('none');
  const serverConnectedRef = useRef(false);

  const resetStreamingState = () => {
    // Canvas visibility is controlled by displayMode state — no manual DOM manipulation needed
    serverConnectedRef.current = false;
    setServerConnected(false);
    setIsReceivingData(false);
    setDisplayMode('none');
    receivedLength_img.current = 0;
    receivedLength_aud.current = 0;
    if (window.FMVP8Player) {
      window.FMVP8Player.foundKeyFrame = false;
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const m = metrics.current;

      // Handle Timeout (5 seconds)
      if (now - lastDataTimeRef.current > 5000) {
        if (serverConnectedRef.current || isReceivingData) {
          resetStreamingState();
          setDisplayTextKey("Stream_is_offline");
        }
      }

      // Traffic consumption log
      if (m.bytesReceived > 0) {
        const mbps = (m.bytesReceived * 8) / (1024 * 1024);
        console.log(
          `%c[Stream] Bitrate: ${mbps.toFixed(2)} Mbps | FPS: ${m.framesRendered} | Dropped: ${m.framesDropped} | Audio: ${audioCtxRef.current?.state || 'off'}`,
          "color: #183a68; font-weight: bold;"
        );
      }

      m.bytesReceived = 0;
      m.framesRendered = 0;
      m.framesDropped = 0;
    }, 1000);

    return () => clearInterval(interval);
  }, [isReceivingData]);

  // Assign the React-managed canvas ref to FMVP8Player once on mount
  useEffect(() => {
    if (vp8CanvasRef.current && window.FMVP8Player) {
      window.FMVP8Player.canvas = vp8CanvasRef.current;
      window.FMVP8Player.context = vp8CanvasRef.current.getContext('2d');
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const userData = await getUserDataById(streamingUserId);
      setStreamingUserData(userData);
    };
    if (streamingUserId !== userId) fetchData();
    connectAsRoom();
    return () => {
      fmWebSocketDisconnect();
      if (audioCtxRef.current) audioCtxRef.current.close();
    };
  }, [streamingUserId]);

  useEffect(() => {
    const updateText = () => setDisplayText(t(displayTextKey));
    updateText();
    i18n.on("languageChanged", updateText);
    const handleFullScreenChange = () => {
      setFullScreenMode(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullScreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullScreenChange);
      i18n.off("languageChanged", updateText);
    };
  }, [displayTextKey, t]);

  const toggleFullScreen = () => {
    if (!fullScreenMode) {
      if (document.documentElement.requestFullscreen) document.documentElement.requestFullscreen();
    } else {
      if (document.exitFullscreen) document.exitFullscreen();
    }
    setFullScreenMode(!fullScreenMode);
  };

  function FMWebSocketEvent(inputType: string, inputVariable: string) {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send("fmevent" + ":" + inputType + ":" + inputVariable);
    }
  }

  function connectAsRoom() {
    fmWebSocketDisconnect();
    fmWebSocketConnect("Room", streamingUserId!);
  }

  const fmWebSocketDisconnect = () => {
    if (wsRef.current !== null) {
      wsRef.current.close();
      wsRef.current = null;
    }
    resetStreamingState();
  };

  const fmWebSocketConnect = (inputNetworkType: string, inputRoomName: string) => {
    let IP = `${API_BASE_URL}/ws/`.replace("http://", "ws://").replace("https://", "wss://");
    const fullUrl = `${IP}${streamingUserId}?token=${token}`;
    const ws = new WebSocket(fullUrl);
    wsRef.current = ws;
    ws.binaryType = "arraybuffer";

    ws.onopen = () => {
      FMWebSocketEvent("networkType", inputNetworkType);
      console.log("**connected to server");
    };
    ws.onclose = () => resetStreamingState();
    ws.onerror = () => resetStreamingState();

    ws.onmessage = (evt) => {
      const data = evt.data;
      if (typeof data === "string") {
        if (data.includes("fmevent:")) {
          const decoded = data.split(":");
          const type = decoded[1];
          switch (type) {
            case "OnJoinedLobbyEvent":
              FMWebSocketEvent("roomName", inputRoomName);
              break;
            case "OnLostServerEvent":
              resetStreamingState();
              setDisplayTextKey("Stream_is_offline");
              break;
            case "OnFoundServerEvent":
              setDisplayTextKey("Loading...");
              break;
          }
        }
        return;
      }

      if (data instanceof ArrayBuffer) {
        lastDataTimeRef.current = Date.now();
        metrics.current.bytesReceived += data.byteLength;
        const _byteRaw = new Uint8Array(data);

        let _byteData: Uint8Array;
        if (_byteRaw[1] === 3) {
          const _wsidLen = (_byteRaw[4] & 255) + ((_byteRaw[5] & 255) << 8);
          _byteData = _byteRaw.slice(_wsidLen + 6);
        } else {
          _byteData = _byteRaw.slice(4);
        }

        if (_byteRaw[0] === 0 && _byteData.length >= 14) {
          const _label = (_byteData[0] & 255) + ((_byteData[1] & 255) << 8);

          if (_label === label_img) {
            const _dataID = (_byteData[2] & 255) + ((_byteData[3] & 255) << 8);
            if (_dataID !== dataID_img.current) {
              if (receivedLength_img.current > 0 && receivedLength_img.current < dataLength_img.current) {
                metrics.current.framesDropped++;
              }
              receivedLength_img.current = 0;
              dataID_img.current = _dataID;
            }

            dataLength_img.current = (_byteData[4] & 255) + ((_byteData[5] & 255) << 8) + ((_byteData[6] & 255) << 16) + ((_byteData[7] & 255) << 24);
            const _offset = (_byteData[8] & 255) + ((_byteData[9] & 255) << 8) + ((_byteData[10] & 255) << 16) + ((_byteData[11] & 255) << 24);
            const _GZipMode = _byteData[12] === 1;
            const _isVP8Chunk = _byteData[13] === 21;
            const isDesktopFrame = _byteData[14] !== 0;
            const metaByteLength = isDesktopFrame ? 24 : 15;

            if (receivedLength_img.current === 0) {
              if (dataByte_img.current.length !== dataLength_img.current) {
                dataByte_img.current = new Uint8Array(dataLength_img.current);
              }
            }

            const chunk = _byteData.slice(metaByteLength);
            if (receivedLength_img.current === 0 || _dataID === dataID_img.current) {
              if (_offset + chunk.length <= dataByte_img.current.length) {
                dataByte_img.current.set(chunk, _offset);
                receivedLength_img.current += chunk.length;
              }
            }

            if (receivedLength_img.current === dataLength_img.current) {
              if (ReadyToGetFrame_img.current) {
                if (!serverConnected) setServerConnected(true);
                serverConnectedRef.current = true;
                handleVideoData(dataByte_img.current, _GZipMode, _isVP8Chunk);
                if (!isReceivingData) setIsReceivingData(true);
              } else {
                metrics.current.framesDropped++;
              }
            }
          } else if (_label === label_aud) {
            const _dataID = (_byteData[2] & 255) + ((_byteData[3] & 255) << 8) + ((_byteData[4] & 255) << 16) + ((_byteData[5] & 255) << 24);
            if (_dataID !== dataID_aud.current) {
              receivedLength_aud.current = 0;
              dataID_aud.current = _dataID;
            }
            dataLength_aud.current = (_byteData[4] & 255) + ((_byteData[5] & 255) << 8) + ((_byteData[6] & 255) << 16) + ((_byteData[7] & 255) << 24);
            const _offset = (_byteData[8] & 255) + ((_byteData[9] & 255) << 8) + ((_byteData[10] & 255) << 16) + ((_byteData[11] & 255) << 24);
            const _GZipMode = _byteData[12] === 1;

            if (receivedLength_aud.current === 0) {
              if (dataByte_aud.current.length !== dataLength_aud.current) {
                dataByte_aud.current = new Uint8Array(dataLength_aud.current);
              }
            }
            const chunk = _byteData.slice(14);
            if (_offset + chunk.length <= dataByte_aud.current.length) {
              dataByte_aud.current.set(chunk, _offset);
              receivedLength_aud.current += chunk.length;
            }
            if (ReadyToGetFrame_aud.current && receivedLength_aud.current === dataLength_aud.current) {
              processAudioData(dataByte_aud.current, _GZipMode);
            }
          } else {
            // Fallback magic from index.html
            if (ReadyToGetFrame_img.current && _byteData.length > 100) {
              if (!serverConnected) setServerConnected(true);
              serverConnectedRef.current = true;
              handleVideoData(_byteData.slice(), false, true);
              if (!isReceivingData) setIsReceivingData(true);
            }
          }
        }
      }
    };

    const ensureDecoderActive = () => {
      if (window.FMVP8Player) {
        if (!window.FMVP8Player.canvas && vp8CanvasRef.current) {
          window.FMVP8Player.canvas = vp8CanvasRef.current;
          window.FMVP8Player.context = vp8CanvasRef.current.getContext('2d');
        }
        if (window.FMVP8Player.decoder.state === "closed") {
          window.FMVP8Player.decoder = new (window as any).VideoDecoder({
            output: window.FMVP8Player.handleDecodedFrame,
            error: (e: any) => { },
          });
          window.FMVP8Player.decoder.configure({ codec: 'vp8' });
        }
      }
    };

    const handleVideoData = (bytes: Uint8Array, _GZipMode: boolean, _isVP8Chunk: boolean) => {
      ReadyToGetFrame_img.current = false;
      let frameData = bytes;
      if (_GZipMode) {
        try { frameData = pako.inflate(bytes); }
        catch (e) { ReadyToGetFrame_img.current = true; return; }
      }
      try {
        if (_isVP8Chunk) {
          if (displayMode !== 'vp8') setDisplayMode('vp8');
          if (window.FMVP8Player) {
            ensureDecoderActive();
            window.FMVP8Player.FMVP8Decode(frameData);
            metrics.current.framesRendered++;
          }
        } else {
          if (displayMode !== 'img') setDisplayMode('img');
          const blob = new Blob([frameData], { type: "image/jpeg" });
          if (imageRef.current) {
            if (imageRef.current.src.startsWith('blob:')) URL.revokeObjectURL(imageRef.current.src);
            imageRef.current.src = URL.createObjectURL(blob);
            metrics.current.framesRendered++;
          }
        }
      } catch (e) { }
      ReadyToGetFrame_img.current = true;
    };

    const processAudioData = (bytes: Uint8Array, _GZipMode: boolean) => {
      ReadyToGetFrame_aud.current = false;
      let audioData = bytes;
      if (_GZipMode) {
        try { audioData = pako.inflate(bytes); }
        catch (e) { ReadyToGetFrame_aud.current = true; return; }
      }
      const sampleRate = (audioData[0] & 255) + ((audioData[1] & 255) << 8) + ((audioData[2] & 255) << 16) + ((audioData[3] & 255) << 24);
      const channels = (audioData[4] & 255) + ((audioData[5] & 255) << 8) + ((audioData[6] & 255) << 16) + ((audioData[7] & 255) << 24);
      const audioInt16 = new Int16Array(audioData.buffer, audioData.byteOffset + 8, (audioData.length - 8) / 2);
      if (audioInt16.length > 0) playAudio(channels, audioInt16, sampleRate);
      ReadyToGetFrame_aud.current = true;
    };

    const playAudio = (numChannels: number, audioChunks: Int16Array, sampleRate: number) => {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
        audioStartTimeRef.current = audioCtxRef.current.currentTime;
      }
      const ctx = audioCtxRef.current;
      const numSamplesPerChannel = audioChunks.length / numChannels;
      const buffer = ctx.createBuffer(numChannels, numSamplesPerChannel, sampleRate);
      for (let channel = 0; channel < numChannels; channel++) {
        const nowBuffering = buffer.getChannelData(channel);
        for (let i = 0; i < numSamplesPerChannel; i++) {
          const order = i * numChannels + channel;
          nowBuffering[i] = audioChunks[order] / 32767.0;
        }
      }
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(ctx.destination);
      if (audioStartTimeRef.current < ctx.currentTime) audioStartTimeRef.current = ctx.currentTime;
      source.start(audioStartTimeRef.current);
      audioStartTimeRef.current += buffer.duration;
    };
  };

  const hideMissedIcon = (elem: HTMLElement) => {
    const alt = elem.getAttribute("alt") || "";
    const altTextNode = document.createTextNode(alt);
    if (elem.parentNode) {
      elem.parentNode.insertBefore(altTextNode, elem);
      elem.parentNode.removeChild(elem);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height:
          fullScreenMode && serverConnected ? "100%" : "calc(100vh - 81px)",
        paddingTop: fullScreenMode && serverConnected ? "0px" : "140px",
        paddingBottom: fullScreenMode && serverConnected ? "0px" : "20px",
        paddingLeft: fullScreenMode && serverConnected ? "0px" : "20px",
        paddingRight: fullScreenMode && serverConnected ? "0px" : "20px",
      }}
    >
      {(!fullScreenMode || !serverConnected) && (
        <>
          {streamingUserId &&
            streamingUserId !== userId &&
            streamingUserData.email && (
              <div style={{ marginTop: "1vh" }}>
                <span style={{ color: "#183a68" }}>
                  {t("Stream_for_user")}:
                </span>
                {` ${streamingUserData.firstName} ${streamingUserData.lastName} `}
                <span style={{ fontFamily: "VirtualLabQuickSandLight" }}>
                  {` | ${streamingUserData.email} `}
                  {streamingUserData.groupName &&
                    ` | ${streamingUserData.groupName}`}
                  {streamingUserData.groupCountry &&
                    ` | ${streamingUserData.groupCountryCode}`}
                </span>
              </div>
            )}
          <div
            style={{
              backgroundColor: "#183a68",
              width: "80vh",
              paddingBottom: "3px",
            }}
          ></div>
        </>
      )}
      <div
        style={{
          textAlign: "center",
          margin: fullScreenMode && serverConnected ? "0px" : "20px auto",
          height:
            fullScreenMode && serverConnected
              ? "100vh"
              : serverConnected
                ? "100%"
                : "auto",
          width: "100%",
        }}
      >
        <div
          ref={canvasContainerRef}
          style={{
            display: displayMode === 'vp8' && serverConnected ? 'block' : 'none',
            height: fullScreenMode && serverConnected ? "100vh" : "100%",
            textAlign: "center"
          }}
        >
          <canvas
            ref={vp8CanvasRef}
            style={{
              height: fullScreenMode ? "100vh" : "100%",
              width: "auto",
              maxWidth: "100%",
              objectFit: "contain",
            }}
          />
        </div>
        {serverConnected ? (
          <>
            <img
              id="DisplayImg"
              ref={imageRef}
              style={{
                display: displayMode === 'img' ? 'inline-block' : 'none',
                height: fullScreenMode && serverConnected ? "100vh" : "100%",
                width: "auto",
                maxWidth: "100%",
                objectFit: "contain"
              }}
              alt=""
              src=""
            />
          </>
        ) : (
          <span>
            <img
              id="DisplayImg"
              width="0%"
              alt=""
              src=""
              onError={(e) => {
                hideMissedIcon(e.currentTarget);
              }}
            />
            {displayText}
          </span>
        )}
      </div>
      {(!fullScreenMode || !serverConnected) && (
        <div
          style={{
            backgroundColor: "#183a68",
            width: "80vh",
            paddingTop: "3px",
          }}
        ></div>
      )}
      {serverConnected && !fullScreenMode && (
        <button
          className="btn btn-primary mb-2"
          style={{
            height: "5vh",
            maxHeight: "40px",
            maxWidth: "20%",
            minWidth: "10%",
            borderRadius: "20px",
            marginTop: "20px",
            backgroundColor: "#183a68",
            borderColor: "#183a68",
          }}
          onClick={toggleFullScreen}
        >
          {t("Enter_Full_Screen")}
        </button>
      )}
    </div>
  );
};

export default Stream;
